import React from "react";
import styled from "styled-components";
import { Paper } from "@mui/material";

import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";

import LabelBottomNavigation from "./BottomNavigation";
import NavbarComp from "./topNav/NavbarComp";
import { auth } from "../../services/firebase.config";
import { White } from "../theme/colors";
import { shouldRedirectToOnboarding } from "../../services/onboardingCheck";
import mixpanelService from "../../services/MixpanelService";
import postHogService from "../../services/PostHogService";

export const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${(props) => props.$background};
`;

export const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

export const Body = styled.div`
  margin-top: ${(p) =>
    p.$noNavbar ? 0 : "140px"}; /* If no navbar, start at top */
  margin-bottom: ${(p) => (p.$noFooter ? 0 : "100px")};
  overflow-x: hidden;
`;

export const Section = styled.div`
  min-height: 25vh;
  padding: 50px 10px 50px;
`;

export const StickyFooter = styled(Paper)`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  margin: 0;
  box-sizing: border-box;
  z-index: 10; /* Ensure it stays above other elements */
`;

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const lastHandledDeepLink = { current: null };

// Global function to reset deep link state
window.resetDeepLinkState = () => {
  lastHandledDeepLink.current = null;
  console.log("🔄 Deep link state reset");
};

function Layout(props) {
  const currentUser = auth.currentUser;
  const { activeTab, onTabChange, showTabs } = props;

  const navigate = useNavigate();
  const location = useLocation();

  // Function to convert route paths to readable page names
  const getPageName = (pathname) => {
    const path = pathname.toLowerCase();

    // Handle quiz question pages
    if (path.includes("/question/")) {
      if (path.includes("political-alignment"))
        return "Political Alignment Quiz Question";
      if (path.includes("best-date")) return "Best Date Quiz Question";
      if (path.includes("personality-check"))
        return "Personality Check Quiz Question";
      if (path.includes("sex-styles")) return "Sex Styles Quiz Question";
      return "Quiz Question";
    }

    // Handle main routes
    const routeMap = {
      "/": "Landing",
      "/home": "Home",
      "/profile": "Profile",
      "/friends": "Friends",
      "/settings-overview": "Settings Overview",
      "/settings-profile": "Settings Profile",
      "/login": "Login",
      "/save-account": "Save Account",
      "/confirmation": "Confirmation",
      "/terms-of-service": "Terms of Service",
      "/privacy-policy": "Privacy Policy",
      "/delete-my-data": "Delete My Data",
      "/download-app": "Download App",
      "/download": "Download App 2",
      "/age-restriction": "Age Restriction",
      "/onboarding-start": "Onboarding Start",
      "/onboarding-intro": "Onboarding Intro",
      "/onboarding-one": "Onboarding Step 1",
      "/onboarding-one-five": "Onboarding Step 1.5",
      "/onboarding-two": "Onboarding Step 2",
      "/onboarding-three": "Onboarding Step 3",
      "/quiz-intro": "Quiz Intro",
      "/political-alignment": "Political Alignment Quiz Landing",
      "/political-alignment/results": "Political Alignment Results",
      "/best-date": "Best Date Quiz Landing",
      "/best-date/results": "Best Date Results",
      "/personality-check": "Personality Check Quiz Landing",
      "/personality-check/results": "Personality Check Results",
      "/sex-styles": "Sex Styles Quiz Landing",
      "/sex-styles/results": "Sex Styles Results",
    };

    // Handle dynamic routes like /:username (public profiles)
    // Check if it's a simple path (e.g., /username) that's not in our route map
    if (path.startsWith("/") && !path.includes("/", 1) && !routeMap[path]) {
      return "Public Profile";
    }

    return routeMap[path] || "Unknown Page";
  };

  // Track page views automatically
  useEffect(() => {
    const pageName = getPageName(location.pathname);
    const pageViewProps = {
      path: location.pathname,
      search: location.search,
      user_id: currentUser?.uid || "anonymous",
      is_authenticated: !!currentUser && !currentUser.isAnonymous,
      timestamp: new Date().toISOString(),
    };

    mixpanelService.track(pageName, pageViewProps);
    postHogService.track(pageName, pageViewProps);
  }, [location.pathname, location.search, currentUser]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    // Handle custom navigation events from system interactions (e.g., notification taps)
    const handleNavigateEvent = (e) => {
      const path = e?.detail?.path;
      if (typeof path === "string" && path.startsWith("/")) {
        navigate(path);
      }
    };

    const handleDeepLink = ({ url }) => {
      // Only block if it's the exact same URL (prevents double-handling)
      if (lastHandledDeepLink.current === url) {
        console.log("🚫 Ignoring duplicate deep link:", url);
        return;
      }

      console.log("🔥 appUrlOpen triggered with:", url);

      // Ignore Firebase auth verification deep links - they shouldn't trigger navigation
      if (
        url.includes("firebaseauth/link") ||
        url.includes("authType=verifyApp")
      ) {
        console.log("🚫 Ignoring Firebase auth verification deep link:", url);
        return;
      }

      try {
        // Handle both custom scheme (vibe-check://) and universal links (https://)
        let path;
        if (url.startsWith("vibe-check://")) {
          const fullPath = url.replace("vibe-check://", "");
          const [pathPart, query = ""] = fullPath.split("?");
          path = `/${pathPart}${query ? `?${query}` : ""}`;
        } else {
          // Universal link - parse URL properly
          const urlObj = new URL(url);
          path = urlObj.pathname + urlObj.search;
        }

        // Handle /s/ and /p/ prefixed routes (server-side meta routes)
        // These should redirect to the actual app routes
        if (path.startsWith("/s/")) {
          // /s/quiz-name/person?from=123 → navigate to quiz landing
          console.log("🔗 Detected /s/ route, redirecting to quiz");
          // Extract the actual route from the /s/ path
          const parts = path.split("?");
          const pathPart = parts[0]; // /s/personality-check/mehak
          const query = parts[1] ? `?${parts[1]}` : ""; // ?from=123
          const segments = pathPart.split("/").filter(Boolean); // ["s", "personality-check", "mehak"]

          if (segments.length >= 2) {
            const quizType = segments[1];
            // Legacy quizzes use /{quizType}, new dynamic quizzes use /quiz/{quizType}
            const legacyQuizTypes = new Set([
              "best-date",
              "personality-check",
              "political-alignment",
              "sex-styles",
              "attachment-styles",
              "am-i-gay",
              "affection-profile",
              "love-languages",
            ]);
            const isLegacyQuiz = legacyQuizTypes.has(quizType);
            path = isLegacyQuiz
              ? `/${quizType}${query}`
              : `/quiz/${quizType}${query}`;
          }
        } else if (path.startsWith("/p/")) {
          // /p/username → /username (profile page)
          path = path.replace("/p/", "/");
        }

        setTimeout(() => {
          console.log("📍 Navigating to:", path);
          navigate(path);
        }, 1000);

        lastHandledDeepLink.current = url; // Track this URL to prevent duplicates
      } catch (err) {
        console.warn("❌ Could not parse deep link:", url);
      }
    };

    // Handle app state changes for onboarding check
    const handleAppStateChange = async ({ isActive }) => {
      if (isActive && currentUser && !currentUser.isAnonymous) {
        try {
          const needsOnboarding = await shouldRedirectToOnboarding(currentUser);
          if (needsOnboarding) {
            console.log(
              "🔄 App resumed - user needs onboarding, redirecting..."
            );
            navigate("/onboarding-start");
          }
        } catch (error) {
          console.error("Error checking onboarding on app resume:", error);
        }
      }
    };

    window.addEventListener("vc-navigate", handleNavigateEvent);
    CapacitorApp.addListener("appUrlOpen", handleDeepLink);
    CapacitorApp.addListener("appStateChange", handleAppStateChange);

    CapacitorApp.getLaunchUrl().then((res) => {
      if (res?.url) {
        console.log("🚀 Cold start URL:", res.url);
        handleDeepLink(res);
      }
    });

    return () => {
      window.removeEventListener("vc-navigate", handleNavigateEvent);
      CapacitorApp.removeAllListeners();
    };
  }, [navigate, currentUser]);

  if (!isMobile) {
    return (
      <div
        style={{
          height: "100vh",
          width: "100vw",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "black",
          color: "white",
          fontFamily: "Satoshi-Variable",
          textAlign: "center",
          padding: "20px",
        }}
      >
        This app is currently only available on mobile devices. Please open it
        on your phone.
      </div>
    );
  }

  return (
    <Wrapper $background={props.$background || White}>
      {!props.$noNavbar && (
        <NavbarComp
          activeTab={activeTab}
          onTabChange={onTabChange}
          showTabs={showTabs}
          $background={props.$navbarBackground || props.$background || White}
        />
      )}
      <StyledLayout>
        <Body $noNavbar={props.$noNavbar} $noFooter={props.$noFooter}>
          {props.children}
        </Body>
      </StyledLayout>
      {!props.$noFooter && currentUser && !currentUser.isAnonymous && (
        <StickyFooter sx={{ position: "fixed" }} elevation={3}>
          <LabelBottomNavigation
            activeTab={activeTab}
            onTabChange={onTabChange}
          />
        </StickyFooter>
      )}
    </Wrapper>
  );
}

export default Layout;
